/**
 * Manager for physics configuration state with persistence and EventBus integration.
 * Single source of truth for scene-wide physics settings (gravity, friction, restitution).
 */

import { Observable } from "@babylonjs/core";
import { BaseManager } from "../core/BaseManager.js";

/**
 * Configuration for scene-wide physics (gravity, friction, restitution)
 */
export interface PhysicsConfig {
  stageId: string;
  gravity: number;
  friction: number;
  restitution: number;
}

/**
 * Default physics configurations per stage type
 */
const DEFAULT_CONFIGS: Record<string, Omit<PhysicsConfig, "stageId">> = {
  bob: { friction: 0.6, gravity: -9.8, restitution: 0.2 },
  gem: { friction: 0.6, gravity: -9.8, restitution: 0.3 },
  rune: { friction: 0.8, gravity: -9.8, restitution: 0.1 },
};

/**
 * localStorage key prefix for physics configs
 */
const STORAGE_KEY_PREFIX = "tony.nl/physics-config/";

/**
 * Manages physics configuration state with Observable notifications,
 * localStorage persistence, and EventBus integration.
 *
 * Used by both React demo pages (via usePhysicsConfig hook) and
 * Inspector v2 (via useObservableState).
 */
export class PhysicsConfigManager extends BaseManager {
  private readonly _configChangedObservable =
    new Observable<PhysicsConfig | null>();
  private currentConfig: PhysicsConfig | null = null;

  /**
   * Observable that fires when config changes.
   * Subscribe with: manager.configChangedObservable.add(callback)
   */
  get configChangedObservable(): Observable<PhysicsConfig | null> {
    return this._configChangedObservable;
  }

  /**
   * Current physics config. Alias for getCurrentConfig() to support
   * useObservableState pattern: `useObservableState(() => manager.config, ...)`
   */
  get config(): PhysicsConfig | null {
    return this.currentConfig;
  }

  /**
   * Implementation-specific initialization
   */
  protected async onInitialize(): Promise<void> {
    this.emit("physicsConfigManagerReady", {});
  }

  /**
   * Implementation-specific disposal
   */
  protected onDispose(): void {
    this._configChangedObservable.clear();
    this.currentConfig = null;
  }

  /**
   * Get the current physics config
   */
  getCurrentConfig(): PhysicsConfig | null {
    return this.currentConfig;
  }

  /**
   * Get default config for a stage type
   */
  getDefaultConfig(stageId: string): PhysicsConfig {
    const defaults = DEFAULT_CONFIGS[stageId] ?? {
      friction: 0.6,
      gravity: -9.8,
      restitution: 0.2,
    };
    return { stageId, ...defaults };
  }

  /**
   * Called when a stage is loaded. Loads config from localStorage or defaults.
   * @param stageId The stage ID that was loaded
   */
  onStageLoaded(stageId: string): void {
    this.ensureNotDisposed();

    // Try to load from localStorage
    const stored = this.loadFromStorage(stageId);
    if (stored) {
      this.currentConfig = stored;
    } else {
      // Use defaults
      this.currentConfig = this.getDefaultConfig(stageId);
    }

    // Notify observers
    this._configChangedObservable.notifyObservers(this.currentConfig);
  }

  /**
   * Update the current config with partial values.
   * Persists to localStorage and emits physicsConfigChanged event.
   * @param partial Partial config to merge
   */
  updateConfig(partial: Partial<PhysicsConfig>): void {
    this.ensureNotDisposed();

    if (!this.currentConfig) {
      console.warn("[PhysicsConfigManager] No current config to update");
      return;
    }

    // Merge partial into current
    this.currentConfig = {
      ...this.currentConfig,
      ...partial,
      // Ensure stageId doesn't change
      stageId: this.currentConfig.stageId,
    };

    // Persist to localStorage
    this.saveToStorage(this.currentConfig);

    // Notify Observable subscribers (React hooks, Inspector)
    this._configChangedObservable.notifyObservers(this.currentConfig);

    // Emit EventBus event
    this.emit("physicsConfigChanged", this.currentConfig);
  }

  /**
   * Set the full config (replaces current)
   * @param config The new config
   */
  setConfig(config: PhysicsConfig): void {
    this.ensureNotDisposed();

    this.currentConfig = config;

    // Persist to localStorage
    this.saveToStorage(config);

    // Notify Observable subscribers
    this._configChangedObservable.notifyObservers(this.currentConfig);

    // Emit EventBus event
    this.emit("physicsConfigChanged", this.currentConfig);
  }

  /**
   * Reset config to defaults for the current stage
   */
  resetToDefaults(): void {
    this.ensureNotDisposed();

    if (!this.currentConfig) {
      console.warn("[PhysicsConfigManager] No current config to reset");
      return;
    }

    const stageId = this.currentConfig.stageId;

    // Clear from localStorage
    this.clearStorage(stageId);

    // Reset to defaults
    this.currentConfig = this.getDefaultConfig(stageId);

    // Notify Observable subscribers
    this._configChangedObservable.notifyObservers(this.currentConfig);

    // Emit EventBus event
    this.emit("physicsConfigChanged", this.currentConfig);
  }

  /**
   * Load config from localStorage
   */
  private loadFromStorage(stageId: string): PhysicsConfig | null {
    try {
      const key = STORAGE_KEY_PREFIX + stageId;
      const stored = localStorage.getItem(key);
      if (!stored) return null;

      const parsed = JSON.parse(stored) as Partial<PhysicsConfig>;

      // Validate and merge with defaults
      const defaults = this.getDefaultConfig(stageId);
      return {
        ...defaults,
        ...parsed,
        stageId, // Ensure stageId is correct
      };
    } catch {
      console.warn(
        `[PhysicsConfigManager] Failed to load config for ${stageId}`,
      );
      return null;
    }
  }

  /**
   * Save config to localStorage
   */
  private saveToStorage(config: PhysicsConfig): void {
    try {
      const key = STORAGE_KEY_PREFIX + config.stageId;
      const toStore: Partial<PhysicsConfig> = {
        friction: config.friction,
        gravity: config.gravity,
        restitution: config.restitution,
      };
      localStorage.setItem(key, JSON.stringify(toStore));
    } catch {
      console.warn(
        `[PhysicsConfigManager] Failed to save config for ${config.stageId}`,
      );
    }
  }

  /**
   * Clear config from localStorage
   */
  private clearStorage(stageId: string): void {
    try {
      const key = STORAGE_KEY_PREFIX + stageId;
      localStorage.removeItem(key);
    } catch {
      // Ignore errors
    }
  }
}
